import { useMemo } from 'react';
import { requestForQuoteProductName } from '~/utils/requestForQuoteProductName';
import { Navigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ClipSpinner } from '~/components/ui';
import { isQuoteNoLongerAvailableError, useGetQuoteById } from '~/api';
import { QuoteStatus, RequestForQuoteStatus } from '~/graphql/graphql';
import type { GetQuoteByIdQuery } from '~/graphql/graphql';
import { buildProductGroups } from '~/pages/rfq-view/itemGroups';
import { RequestForQuoteSummary } from '~/pages/rfq-view/sections/RequestForQuoteSummary';
import {
  EditableQuoteCard,
  ReadOnlyQuoteCard,
} from '~/pages/rfq-view/QuoteCard';
import { BuyerRequestDetails } from '~/pages/quote-response/BuyerRequestDetails';
import { SellerNegotiationPanel } from './SellerNegotiationPanel';
import { isClosedRequestForQuoteStatus } from '~/utils/requestForQuoteClosed';
import { TitleWithArchivedBadge } from '~/pages/rfq-view/sections/TitleWithArchivedBadge';

const NO_REQUESTED_ITEMS: NonNullable<
  GetQuoteByIdQuery['quote']
>['requestForQuote']['items'] = [];
const NO_QUOTE_ITEMS: NonNullable<GetQuoteByIdQuery['quote']>['items'] = [];

export function QuoteView() {
  const { quoteId } = useParams();

  if (!quoteId) {
    return <Navigate to="/seller-quotes" replace />;
  }

  return <QuoteViewContent quoteId={quoteId} />;
}

function QuoteViewContent({ quoteId }: { quoteId: string }) {
  const { t } = useTranslation('quote-view');
  const { t: tMyQuotes } = useTranslation('my-quotes');
  const { data, isLoading, isError, error } = useGetQuoteById(quoteId);
  const quote = data?.quote;
  const requestForQuote = quote?.requestForQuote;

  const groups = useMemo(
    () =>
      buildProductGroups(
        requestForQuote?.items ?? NO_REQUESTED_ITEMS,
        quote?.items ?? NO_QUOTE_ITEMS
      ),
    [requestForQuote?.items, quote?.items]
  );

  if (isLoading) return <ClipSpinner />;
  if (isError && isQuoteNoLongerAvailableError(error)) {
    return (
      <p className="text-sm font-medium text-neut-sec">
        {t('quote_view.no_longer_available')}
      </p>
    );
  }
  if (isError) {
    return (
      <div>{t('quote_view.loading_error', { message: error.message })}</div>
    );
  }
  if (!quote || !requestForQuote) {
    return <Navigate to="/seller-quotes" replace />;
  }

  const isRequestForQuoteClosed = isClosedRequestForQuoteStatus(
    requestForQuote.status
  );
  const statusLabel = tMyQuotes(`my_quotes.status.${quote.status}`);

  const cardProps = {
    quote,
    groups,
    isRequestForQuoteClosed,
    requestForQuoteUsesTallyPrice: requestForQuote.useTallyPrice,
    requestForQuoteHasCannedTally: Boolean(requestForQuote.cannedTally),
    requestForQuoteCurrency: requestForQuote.currency,
    statusLabel,
  };

  return (
    <div className="flex flex-col gap-6" data-testid="quote-view-heading">
      <TitleWithArchivedBadge
        title={t('quote_view.title', {
          productName:
            requestForQuoteProductName(requestForQuote) ??
            t('quote_view.multiple_products'),
        })}
        isArchived={requestForQuote.status === RequestForQuoteStatus.Archived}
        archivedLabel={t('quote_view.request_archived_by_buyer')}
        badgeTestId="request-archived-badge"
      />
      <RequestForQuoteSummary
        requestForQuote={requestForQuote}
        groups={groups}
        headingKey="rfq_view.buyers_request_heading"
        details={<BuyerRequestDetails requestForQuote={requestForQuote} />}
      />
      {quote.status === QuoteStatus.InProgress ? (
        <EditableQuoteCard {...cardProps} />
      ) : (
        <ReadOnlyQuoteCard {...cardProps} />
      )}
      <SellerNegotiationPanel
        quote={quote}
        groups={groups}
        usesWholeRequestPrice={requestForQuote.useTallyPrice}
        requestForQuoteCurrency={requestForQuote.currency}
      />
    </div>
  );
}
